/** 前端筛选表单字段 */
export interface DamagedLabelLookupInput {
    sortingCodeSegment1?: string;
    sortingCodeSegment2?: string;
    sortingCodeSegment3?: string;
    warehouseId?: string;
    senderName?: string;
    senderAddress?: string;
    senderPostalCode?: string;
    recipientName?: string;
    recipientAddress?: string;
    recipientPostalCode?: string;
    trackingNumber?: string;
    channelOrderNumber?: string;
    /** 运单状态（多选，OverallStatus 枚举名）；不选 = 全部 */
    overallStatuses?: string[];
}

/** GraphQL DamagedParcelFiltersTypeInput，与后端 DamagedParcelFilters 对齐 */
export interface DamagedParcelFiltersInput {
    senderFullName?: string;
    senderAddressText?: string;
    senderPostalCode?: string;
    recipientFullName?: string;
    recipientAddressText?: string;
    recipientPostalCode?: string;
    trackingNumber?: string;
    channelOrderNumber?: string;
    sortingCodeSegment1?: string;
    sortingCodeSegment2?: string;
    sortingCodeSegment3?: string;
    overallStatuses?: string[];
}

export type DamagedLabelLookupScope = 'central' | 'wms';

export const EMPTY_DAMAGED_LABEL_LOOKUP_INPUT: DamagedLabelLookupInput = {};

export const DAMAGED_LABEL_LOOKUP_MAX_FIELD_LENGTH = 50;

/** 除发件/收件邮编，有值的筛选字段至少需要的字符数 */
export const DAMAGED_LABEL_LOOKUP_MIN_QUERY_LENGTH = 3;

/** 文本类筛选字段（运单状态是多选数组，单独处理） */
export type DamagedLabelLookupFilterField = Exclude<keyof DamagedLabelLookupInput, 'overallStatuses'>;

const FILTER_FIELDS: DamagedLabelLookupFilterField[] = [
    'sortingCodeSegment1',
    'sortingCodeSegment2',
    'sortingCodeSegment3',
    'senderName',
    'senderAddress',
    'senderPostalCode',
    'recipientName',
    'recipientAddress',
    'recipientPostalCode',
    'trackingNumber',
    'channelOrderNumber',
];

/** 发件/收件邮编、分拣码段不限制最小长度，其余字段有值时至少 3 个字符 */
const MIN_QUERY_LENGTH_FIELDS: DamagedLabelLookupFilterField[] = FILTER_FIELDS.filter(
    (field) =>
        field !== 'senderPostalCode' &&
        field !== 'recipientPostalCode' &&
        field !== 'sortingCodeSegment1' &&
        field !== 'sortingCodeSegment2' &&
        field !== 'sortingCodeSegment3'
);

const TRIM_FIELDS: DamagedLabelLookupFilterField[] = [
    ...FILTER_FIELDS,
    'warehouseId',
];

export function trimDamagedLabelLookupInput(
    input: DamagedLabelLookupInput
): DamagedLabelLookupInput {
    const result: DamagedLabelLookupInput = {};
    TRIM_FIELDS.forEach((field) => {
        const value = input[field]?.trim();
        if (value) {
            result[field] = value;
        }
    });
    if (input.overallStatuses?.length) {
        result.overallStatuses = [...input.overallStatuses];
    }
    return result;
}

/** 前端表单 → GraphQL filters */
export function toDamagedParcelFilters(
    input: DamagedLabelLookupInput
): DamagedParcelFiltersInput {
    const trimmed = trimDamagedLabelLookupInput(input);
    return {
        senderFullName: trimmed.senderName,
        senderAddressText: trimmed.senderAddress,
        senderPostalCode: trimmed.senderPostalCode,
        recipientFullName: trimmed.recipientName,
        recipientAddressText: trimmed.recipientAddress,
        recipientPostalCode: trimmed.recipientPostalCode,
        trackingNumber: trimmed.trackingNumber,
        channelOrderNumber: trimmed.channelOrderNumber,
        sortingCodeSegment1: trimmed.sortingCodeSegment1,
        sortingCodeSegment2: trimmed.sortingCodeSegment2,
        sortingCodeSegment3: trimmed.sortingCodeSegment3,
        overallStatuses: trimmed.overallStatuses,
    };
}

/** 返回 i18n key；通过则返回 null。运单状态是收窄条件，不计入「至少一个查询条件」 */
export function validateDamagedLabelLookupInput(
    input: DamagedLabelLookupInput
): string | null {
    const trimmed = trimDamagedLabelLookupInput(input);
    const filledCount = FILTER_FIELDS.filter((field) => trimmed[field]).length;

    if (filledCount === 0) {
        return 'damagedLabelLookup.validation.atLeastOneField';
    }

    for (const field of MIN_QUERY_LENGTH_FIELDS) {
        const value = trimmed[field];
        if (value && value.length < DAMAGED_LABEL_LOOKUP_MIN_QUERY_LENGTH) {
            return 'damagedLabelLookup.validation.minQueryLength';
        }
    }

    for (const field of FILTER_FIELDS) {
        const value = trimmed[field];
        if (value && value.length > DAMAGED_LABEL_LOOKUP_MAX_FIELD_LENGTH) {
            return 'damagedLabelLookup.validation.maxLength';
        }
    }

    return null;
}
